import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Phone, MapPin, Headphones, Package } from 'lucide-react';
import { motion } from 'framer-motion';

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=85&w=2000"
          alt="Container ship with cargo and trucks at port"
          className="w-full h-full object-cover"
        />
        {/* Dark gradient overlay from left to right */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/90 to-navy-900/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40 w-full">
        <div className="max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white uppercase tracking-tight leading-[1.1]"
          >
            Your Freight.{' '}
            <span className="text-orange-500">Delivered.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg sm:text-xl text-slate-300 font-body max-w-2xl leading-relaxed"
          >
            LTL, FTL, oversized, and specialized loads—moved with precision across all 48 states. No guesswork. No delays. Just reliable freight solutions from a team that delivers.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/quote" data-testid="hero-quote-btn">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-body font-bold text-base uppercase tracking-wide px-8 py-6 rounded-sm shadow-lg hover:shadow-orange-500/40 transition-shadow duration-300 group"
              >
                Get a Freight Quote
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
            </Link>
            <a href="tel:+18888984591" data-testid="hero-phone-btn">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-navy-900 font-body font-bold text-base uppercase tracking-wide px-8 py-6 rounded-sm transition-colors duration-300"
              >
                <Phone className="mr-2 w-5 h-5" />
                Speak with a Specialist
              </Button>
            </a>
          </motion.div>

          {/* Stats Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 flex flex-wrap items-center gap-6 lg:gap-10"
          >
            <div className="flex items-center gap-3 bg-navy-900/60 backdrop-blur-sm px-5 py-3 rounded-lg border border-navy-700/50">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <span className="font-heading text-2xl font-bold text-white">48</span>
                <p className="text-slate-400 font-body text-sm">States Covered</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-navy-900/60 backdrop-blur-sm px-5 py-3 rounded-lg border border-navy-700/50">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Headphones className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <span className="font-heading text-2xl font-bold text-white">24/7</span>
                <p className="text-slate-400 font-body text-sm">Support Available</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-navy-900/60 backdrop-blur-sm px-5 py-3 rounded-lg border border-navy-700/50">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <span className="font-heading text-2xl font-bold text-white">10K+</span>
                <p className="text-slate-400 font-body text-sm">Loads Coordinated</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
